import {
  sedrApi,
  type SedrStarterSetDetail,
  type SedrStarterSetSummary,
  type SedrStarterSetType,
} from '../../api/sedr';
import {
  buildSedrLibraryUrl,
  onAppNavigate,
  parseSedrLibraryUrl,
  pushAppUrl,
  type SedrLibraryTab,
} from '../../app/navigationState';
import { applyI18n, t } from '../../i18n';
import { bindLocaleRefresh } from '../../i18n/bindLocaleRefresh';
import { showConfirmDialog } from '../../ui/confirmDialog';
import { iconImg } from '../../ui/icons';
import { showSedrCreateStarterSetItemDialog } from './sedrCreateStarterSetItemDialog';
import { escapeHtml } from './sedrEscape';
import { sedrErrorMessageKey } from './sedrErrors';
import { showSedrLibraryImportHubDialog } from './sedrLibraryImportHubDialog';
import { showSedrLibrarySetDialog } from './sedrLibrarySetDialog';
import { showSedrLibraryFolderDialog } from './sedrLibraryFolderDialog';
import {
  applySidebarCollapsed,
  createActivePanelControl,
  effectiveSidebarCollapsed,
  readSidebarCollapsed,
  renderSedrRoomChromeActions,
  SEDR_MOBILE_LAYOUT_MQ,
  writeSidebarCollapsed,
  type SedrChromeTab,
} from './sedrRoomChrome';
import { enhanceDataTableCards } from '../../ui/dataTableCards';
import { folderDepth, sortedFolderTree } from './sedrStarterSetFolders';

function tabToSetType(tab: SedrLibraryTab): SedrStarterSetType {
  return tab === 'folders' ? 'Folders' : 'Requirements';
}

function libraryNavTabs(): SedrChromeTab[] {
  return [
    { id: 'requirements', labelKey: 'sedr.library.tabRequirementsSets' },
    { id: 'folders', labelKey: 'sedr.library.tabFolderSets' },
  ];
}

function sidebarHtml(): string {
  return `
    <aside class="sedr-sidebar" data-sedr-library-sidebar>
      <a href="/sedr" class="button ghost sedr-sidebar__back" data-i18n="sedr.library.backToRooms"></a>
      <h2 class="sedr-sidebar__heading" data-i18n="sedr.library.navHeading"></h2>
      <nav class="sedr-sidebar-nav" data-i18n-aria-label="sedr.library.navHeading">
        <a href="/sedr/library/requirements" class="sedr-sidebar-link" data-tab="requirements" data-i18n="sedr.library.tabRequirementsSets"></a>
        <a href="/sedr/library/folders" class="sedr-sidebar-link" data-tab="folders" data-i18n="sedr.library.tabFolderSets"></a>
      </nav>
    </aside>
  `;
}

/** Mount library UI into the SEDR workspace room pane (list ↔ library expand). */
export function mountSedrLibrary(host: HTMLElement): () => void {
  host.replaceChildren();
  host.innerHTML = `
    <div class="sedr-room-layout sedr-library-layout" data-sedr-library-root data-help-context="sedr-library">
      ${sidebarHtml()}
      <div class="sedr-room-main files-browse-main">
        <div class="files-browse-chrome sedr-room-chrome">
          <div class="files-browse-chrome__identity">
            <a href="/sedr" class="button ghost sedr-library-chrome-back" data-i18n="sedr.library.backToRooms"></a>
            <h2 class="files-browse-chrome__title" data-sedr-library-chrome-title data-i18n="sedr.library.navHeading"></h2>
            <span class="sedr-room-chrome__panel-slot" data-sedr-library-panel-slot></span>
          </div>
          <div class="files-browse-chrome__actions" data-sedr-library-chrome-actions></div>
        </div>
        <div class="sedr-room-main__body sedr-library-body">
          <p class="error-text" data-sedr-library-error hidden></p>
          <div data-sedr-library-content></div>
        </div>
      </div>
    </div>
  `;
  applyI18n(host);

  const root = host.querySelector('[data-sedr-library-root]') as HTMLElement;
  const sidebar = host.querySelector('[data-sedr-library-sidebar]') as HTMLElement;
  const contentEl = host.querySelector('[data-sedr-library-content]') as HTMLElement;
  const errorEl = host.querySelector('[data-sedr-library-error]') as HTMLParagraphElement;
  const chromeTitle = host.querySelector('[data-sedr-library-chrome-title]') as HTMLElement;
  const panelSlot = host.querySelector('[data-sedr-library-panel-slot]') as HTMLElement;
  const chromeActions = host.querySelector('[data-sedr-library-chrome-actions]') as HTMLElement;

  let activeTab: SedrLibraryTab = 'requirements';
  let mountedSetId: string | null = null;
  let contentRenderToken = 0;
  let disposed = false;
  let sidebarPreference = readSidebarCollapsed();
  let chromeActionsCleanup: (() => void) | null = null;
  const sidebarMq = window.matchMedia(SEDR_MOBILE_LAYOUT_MQ);
  const isSidebarCollapsed = () => effectiveSidebarCollapsed(sidebarPreference);

  const showError = (cause?: unknown) => {
    errorEl.hidden = false;
    errorEl.textContent = t(cause !== undefined ? sedrErrorMessageKey(cause) : 'status.error');
  };

  const clearError = () => {
    errorEl.hidden = true;
    errorEl.textContent = '';
  };

  const selectLibraryTab = (tabId: string) => {
    const tab = tabId === 'folders' ? 'folders' : 'requirements';
    pushAppUrl(buildSedrLibraryUrl({ tab }));
  };

  const updateLibraryChrome = () => {
    if (disposed) return;
    const sidebarCollapsed = isSidebarCollapsed();
    applySidebarCollapsed(root, sidebar, sidebarCollapsed);
    root.classList.toggle('sedr-library-layout--nav-hidden', sidebarCollapsed);

    panelSlot.replaceChildren(createActivePanelControl({
      activeTab,
      tabs: libraryNavTabs(),
      navHidden: sidebarCollapsed,
      onSelect: selectLibraryTab,
    }));
    applyI18n(panelSlot);

    chromeActionsCleanup?.();
    const { fragment, cleanup } = renderSedrRoomChromeActions({
      fullscreenTarget: host,
      sidebarCollapsed,
      showFullscreenToggle: false,
      onToggleSidebar: () => {
        sidebarPreference = !sidebarPreference;
        writeSidebarCollapsed(sidebarPreference);
        updateLibraryChrome();
      },
      helpTopic: 'sedr-library',
    });
    chromeActionsCleanup = cleanup;
    chromeActions.replaceChildren(fragment);
    applyI18n(chromeActions);
  };

  const syncSidebars = () => {
    for (const link of host.querySelectorAll<HTMLAnchorElement>('.sedr-sidebar-link[data-tab]')) {
      const tab = link.dataset.tab as SedrLibraryTab;
      const active = tab === activeTab && !mountedSetId;
      link.classList.toggle('sedr-sidebar-link--active', active);
      link.href = buildSedrLibraryUrl({ tab });
    }
    updateLibraryChrome();
  };

  const syncChrome = (title: string | { i18n: string }) => {
    if (typeof title === 'string') {
      chromeTitle.removeAttribute('data-i18n');
      chromeTitle.textContent = title;
      return;
    }
    chromeTitle.setAttribute('data-i18n', title.i18n);
    applyI18n(chromeTitle);
  };

  const renderStarterSetList = async (tab: SedrLibraryTab) => {
    const token = ++contentRenderToken;
    clearError();
    syncChrome({ i18n: 'sedr.library.navHeading' });
    syncSidebars();

    let sets: SedrStarterSetSummary[] = [];
    try {
      sets = await sedrApi.listStarterSets(tabToSetType(tab));
    } catch (cause) {
      if (token !== contentRenderToken || disposed) return;
      showError(cause);
      return;
    }
    if (token !== contentRenderToken || disposed) return;

    const createKey = tab === 'folders'
      ? 'sedr.library.createFolderSet'
      : 'sedr.library.createRequirementsSet';
    const emptyKey = tab === 'folders'
      ? 'sedr.library.emptyFolderSets'
      : 'sedr.library.emptyRequirementsSets';

    contentEl.innerHTML = `
      <div class="sedr-library-toolbar">
        <button type="button" class="button primary" id="sedr-library-create-set">
          <span class="button__icon"></span>
          <span data-i18n="${createKey}"></span>
        </button>
      </div>
      <div class="files-table-wrap" id="sedr-library-set-list"></div>
    `;
    contentEl.querySelector('#sedr-library-create-set .button__icon')?.append(iconImg('sedr', 18));
    applyI18n(contentEl);

    const listEl = contentEl.querySelector('#sedr-library-set-list') as HTMLElement;
    if (sets.length === 0) {
      listEl.innerHTML = `<p class="muted" data-i18n="${emptyKey}"></p>`;
      applyI18n(listEl);
    } else {
      listEl.innerHTML = `
        <table class="data-table">
          <thead>
            <tr>
              <th data-i18n="sedr.library.colName"></th>
              <th data-i18n="sedr.library.colItems"></th>
              <th data-i18n="sedr.library.colUpdated"></th>
              <th class="data-table__actions-heading" data-i18n="sedr.list.colActions"></th>
            </tr>
          </thead>
          <tbody></tbody>
        </table>
      `;
      applyI18n(listEl);
      const tbody = listEl.querySelector('tbody') as HTMLTableSectionElement;
      for (const set of sets) {
        const tr = document.createElement('tr');
        const nameTd = document.createElement('td');
        const link = document.createElement('a');
        link.href = buildSedrLibraryUrl({ tab, setId: set.id });
        link.textContent = set.name;
        nameTd.append(link);
        tr.append(nameTd);

        const itemsTd = document.createElement('td');
        itemsTd.textContent = String(set.itemCount);
        tr.append(itemsTd);

        const updatedTd = document.createElement('td');
        updatedTd.textContent = new Date(set.updatedAt).toLocaleString();
        tr.append(updatedTd);

        const actionsTd = document.createElement('td');
        actionsTd.className = 'data-table__actions';
        const actionsInner = document.createElement('div');
        actionsInner.className = 'data-table__actions-inner';

        const editBtn = document.createElement('a');
        editBtn.href = buildSedrLibraryUrl({ tab, setId: set.id });
        editBtn.className = 'button subtle';
        editBtn.setAttribute('data-i18n', 'common.edit');

        const deleteBtn = document.createElement('button');
        deleteBtn.type = 'button';
        deleteBtn.className = 'button subtle';
        deleteBtn.setAttribute('data-i18n', 'common.delete');
        deleteBtn.addEventListener('click', async () => {
          if (!(await showConfirmDialog({
            titleKey: tab === 'folders'
              ? 'sedr.library.deleteFolderSetTitle'
              : 'sedr.library.deleteRequirementsSetTitle',
            messageKey: tab === 'folders'
              ? 'sedr.library.deleteFolderSetMessage'
              : 'sedr.library.deleteRequirementsSetMessage',
            messageParams: { name: set.name },
            confirmLabelKey: 'common.delete',
            confirmClassName: 'primary',
          }))) {
            return;
          }
          try {
            await sedrApi.deleteStarterSet(set.id);
            if (mountedSetId === set.id) {
              pushAppUrl(buildSedrLibraryUrl({ tab }));
            } else {
              await renderStarterSetList(tab);
            }
          } catch (cause) {
            showError(cause);
          }
        });

        actionsInner.append(editBtn, deleteBtn);
        actionsTd.append(actionsInner);
        tr.append(actionsTd);
        tbody.append(tr);
      }
      applyI18n(tbody);
      const setListTable = listEl.querySelector('table.data-table') as HTMLTableElement | null;
      if (setListTable) {
        enhanceDataTableCards(setListTable);
      }
    }

    contentEl.querySelector('#sedr-library-create-set')?.addEventListener('click', () => {
      showSedrLibrarySetDialog({
        mode: 'create',
        setType: tabToSetType(tab),
        onDone: async (setId) => {
          pushAppUrl(buildSedrLibraryUrl({ tab, setId }));
        },
      });
    });
  };

  const mountRequirementsEditor = async (setId: string, tab: SedrLibraryTab) => {
    const token = ++contentRenderToken;
    clearError();
    syncSidebars();

    const renderEditor = async () => {
      clearError();
      let detail: SedrStarterSetDetail;
      try {
        detail = await sedrApi.getStarterSet(setId);
      } catch (cause) {
        if (token !== contentRenderToken || disposed) return;
        showError(cause);
        return;
      }
      if (token !== contentRenderToken || disposed || mountedSetId !== setId) return;

      syncChrome(detail.name);

      contentEl.innerHTML = `
        <div class="sedr-library-toolbar sedr-library-toolbar--back">
          <a href="${buildSedrLibraryUrl({ tab })}" class="button ghost" data-i18n="sedr.library.backToRequirementsSets"></a>
        </div>
        <div class="sedr-library-toolbar">
          <button type="button" class="button subtle" id="sedr-library-rename-set" data-i18n="sedr.library.renameRequirementsSet"></button>
          <button type="button" class="button primary" id="sedr-library-add-item" data-i18n="sedr.library.addItem"></button>
          <button type="button" class="button subtle" id="sedr-library-import-items" data-i18n="sedr.import.button"></button>
        </div>
        <h3 class="sedr-library-set-name">${escapeHtml(detail.name)}</h3>
        <p class="muted sedr-library-set-desc">${escapeHtml(detail.description ?? '')}</p>
        <div class="files-table-wrap">
          <table class="data-table">
            <thead>
              <tr>
                <th data-i18n="sedr.table.reqId"></th>
                <th data-i18n="sedr.table.title"></th>
                <th data-i18n="sedr.table.category"></th>
                <th data-i18n="sedr.table.owner"></th>
                <th class="data-table__actions-heading" data-i18n="sedr.list.colActions"></th>
              </tr>
            </thead>
            <tbody id="sedr-library-set-items"></tbody>
          </table>
        </div>
      `;
      applyI18n(contentEl);

      contentEl.querySelector('#sedr-library-rename-set')?.addEventListener('click', () => {
        showSedrLibrarySetDialog({
          mode: 'rename',
          setType: 'Requirements',
          setId,
          initialName: detail.name,
          initialDescription: detail.description,
          onDone: () => renderEditor(),
        });
      });

      contentEl.querySelector('#sedr-library-add-item')?.addEventListener('click', () => {
        showSedrCreateStarterSetItemDialog({
          setId,
          onCreated: () => renderEditor(),
        });
      });

      contentEl.querySelector('#sedr-library-import-items')?.addEventListener('click', () => {
        showSedrLibraryImportHubDialog({
          setId,
          onDone: () => {
            void renderEditor();
          },
        });
      });

      const tbody = contentEl.querySelector('#sedr-library-set-items') as HTMLTableSectionElement;
      if (detail.items.length === 0) {
        const tr = document.createElement('tr');
        const td = document.createElement('td');
        td.colSpan = 5;
        td.className = 'muted';
        td.setAttribute('data-i18n', 'sedr.library.emptyItems');
        tr.append(td);
        tbody.append(tr);
        applyI18n(tr);
      } else {
        for (const item of detail.items) {
          const tr = document.createElement('tr');
          tr.innerHTML = `
            <td><code></code></td>
            <td></td>
            <td></td>
            <td></td>
            <td class="data-table__actions"></td>
          `;
          tr.querySelector('code')!.textContent = item.requirementCode;
          tr.children[1].textContent = item.title;
          tr.children[2].textContent = item.category;
          tr.children[3].textContent = item.ownerName ?? '—';
          const deleteBtn = document.createElement('button');
          deleteBtn.type = 'button';
          deleteBtn.className = 'button subtle';
          deleteBtn.setAttribute('data-i18n', 'common.delete');
          deleteBtn.addEventListener('click', async () => {
            if (!(await showConfirmDialog({
              titleKey: 'sedr.library.deleteItemTitle',
              messageKey: 'sedr.library.deleteItemMessage',
              messageParams: { code: item.requirementCode },
              confirmLabelKey: 'common.delete',
              confirmClassName: 'primary',
            }))) {
              return;
            }
            try {
              await sedrApi.deleteStarterSetItem(setId, item.id);
              await renderEditor();
            } catch (cause) {
              showError(cause);
            }
          });
          (tr.children[4] as HTMLElement).append(deleteBtn);
          tbody.append(tr);
        }
        applyI18n(tbody);
        const itemsTable = contentEl.querySelector('table.data-table') as HTMLTableElement | null;
        if (itemsTable) {
          enhanceDataTableCards(itemsTable);
        }
      }
    };

    await renderEditor();
  };

  const mountFolderEditor = async (setId: string, tab: SedrLibraryTab) => {
    const token = ++contentRenderToken;
    clearError();
    syncSidebars();

    const renderEditor = async () => {
      clearError();
      let detail: SedrStarterSetDetail;
      try {
        detail = await sedrApi.getStarterSet(setId);
      } catch (cause) {
        if (token !== contentRenderToken || disposed) return;
        showError(cause);
        return;
      }
      if (token !== contentRenderToken || disposed || mountedSetId !== setId) return;

      syncChrome(detail.name);
      const folders = sortedFolderTree(detail.folders);

      contentEl.innerHTML = `
        <div class="sedr-library-toolbar sedr-library-toolbar--back">
          <a href="${buildSedrLibraryUrl({ tab })}" class="button ghost" data-i18n="sedr.library.backToFolderSets"></a>
        </div>
        <div class="sedr-library-toolbar">
          <button type="button" class="button subtle" id="sedr-library-rename-set" data-i18n="sedr.library.renameFolderSet"></button>
          <button type="button" class="button primary" id="sedr-library-add-folder" data-i18n="sedr.library.addFolder"></button>
        </div>
        <h3 class="sedr-library-set-name">${escapeHtml(detail.name)}</h3>
        <p class="muted sedr-library-set-desc">${escapeHtml(detail.description ?? '')}</p>
        <div class="files-table-wrap">
          <table class="data-table">
            <thead>
              <tr>
                <th data-i18n="sedr.library.colFolderName"></th>
                <th class="data-table__actions-heading" data-i18n="sedr.list.colActions"></th>
              </tr>
            </thead>
            <tbody id="sedr-library-set-folders"></tbody>
          </table>
        </div>
      `;
      applyI18n(contentEl);

      contentEl.querySelector('#sedr-library-rename-set')?.addEventListener('click', () => {
        showSedrLibrarySetDialog({
          mode: 'rename',
          setType: 'Folders',
          setId,
          initialName: detail.name,
          initialDescription: detail.description,
          onDone: () => renderEditor(),
        });
      });

      contentEl.querySelector('#sedr-library-add-folder')?.addEventListener('click', () => {
        showSedrLibraryFolderDialog({
          setId,
          folders: detail.folders,
          onDone: () => renderEditor(),
        });
      });

      const tbody = contentEl.querySelector('#sedr-library-set-folders') as HTMLTableSectionElement;
      if (folders.length === 0) {
        const tr = document.createElement('tr');
        const td = document.createElement('td');
        td.colSpan = 2;
        td.className = 'muted';
        td.setAttribute('data-i18n', 'sedr.library.emptyFolders');
        tr.append(td);
        tbody.append(tr);
        applyI18n(tr);
      } else {
        for (const folder of folders) {
          const tr = document.createElement('tr');
          const nameTd = document.createElement('td');
          const depth = folderDepth(folders, folder.id);
          nameTd.style.paddingLeft = `${12 + depth * 18}px`;
          nameTd.textContent = folder.name;
          tr.append(nameTd);

          const actionsTd = document.createElement('td');
          actionsTd.className = 'data-table__actions';
          const actionsInner = document.createElement('div');
          actionsInner.className = 'data-table__actions-inner';

          const addChildBtn = document.createElement('button');
          addChildBtn.type = 'button';
          addChildBtn.className = 'button subtle';
          addChildBtn.setAttribute('data-i18n', 'sedr.library.addChildFolder');
          addChildBtn.addEventListener('click', () => {
            showSedrLibraryFolderDialog({
              setId,
              folders: detail.folders,
              parentId: folder.id,
              onDone: () => renderEditor(),
            });
          });

          const renameBtn = document.createElement('button');
          renameBtn.type = 'button';
          renameBtn.className = 'button subtle';
          renameBtn.setAttribute('data-i18n', 'files.owner.rename');
          renameBtn.addEventListener('click', () => {
            showSedrLibraryFolderDialog({
              setId,
              folders: detail.folders,
              folderId: folder.id,
              initialName: folder.name,
              parentId: folder.parentId,
              onDone: () => renderEditor(),
            });
          });

          const deleteBtn = document.createElement('button');
          deleteBtn.type = 'button';
          deleteBtn.className = 'button subtle';
          deleteBtn.setAttribute('data-i18n', 'common.delete');
          deleteBtn.addEventListener('click', async () => {
            if (!(await showConfirmDialog({
              titleKey: 'sedr.library.deleteFolderTitle',
              messageKey: 'sedr.library.deleteFolderMessage',
              messageParams: { name: folder.name },
              confirmLabelKey: 'common.delete',
              confirmClassName: 'primary',
            }))) {
              return;
            }
            try {
              await sedrApi.deleteStarterSetFolder(setId, folder.id);
              await renderEditor();
            } catch (cause) {
              showError(cause);
            }
          });

          actionsInner.append(addChildBtn, renameBtn, deleteBtn);
          actionsTd.append(actionsInner);
          tr.append(actionsTd);
          tbody.append(tr);
        }
        applyI18n(tbody);
        const foldersTable = contentEl.querySelector('table.data-table') as HTMLTableElement | null;
        if (foldersTable) {
          enhanceDataTableCards(foldersTable);
        }
      }
    };

    await renderEditor();
  };

  const syncRoute = () => {
    if (disposed) return;
    const state = parseSedrLibraryUrl() ?? { tab: 'requirements' as const, setId: null };
    activeTab = state.tab;
    const setId = state.setId;

    if (!setId) {
      if (mountedSetId !== null) {
        mountedSetId = null;
      }
      void renderStarterSetList(activeTab);
      return;
    }

    if (setId === mountedSetId) {
      // Same set already mounted; still refresh editor if locale/route tab needs sync.
      syncSidebars();
      return;
    }

    mountedSetId = setId;
    void (async () => {
      try {
        const detail = await sedrApi.getStarterSet(setId);
        if (disposed || mountedSetId !== setId) return;
        const expectedTab: SedrLibraryTab = detail.setType === 'Folders' ? 'folders' : 'requirements';
        if (activeTab !== expectedTab) {
          activeTab = expectedTab;
          // Keep mountedSetId so the subsequent navigate early-returns after we mount below.
          pushAppUrl(buildSedrLibraryUrl({ tab: expectedTab, setId }), { replace: true });
        }
        if (expectedTab === 'folders') {
          await mountFolderEditor(setId, expectedTab);
        } else {
          await mountRequirementsEditor(setId, expectedTab);
        }
        syncSidebars();
      } catch (cause) {
        if (disposed || mountedSetId !== setId) return;
        showError(cause);
      }
    })();
    syncSidebars();
  };

  const onLibraryRoute = () => syncRoute();
  host.addEventListener('sedr:library-route', onLibraryRoute);

  const unbindNav = onAppNavigate(() => {
    if (disposed || !root.isConnected) {
      unbindNav();
      return;
    }
    if (!parseSedrLibraryUrl()) return;
    syncRoute();
  });

  const unbindLocale = bindLocaleRefresh(() => {
    if (disposed) return;
    applyI18n(host);
    updateLibraryChrome();
    if (mountedSetId) {
      if (activeTab === 'folders') {
        void mountFolderEditor(mountedSetId, activeTab);
      } else {
        void mountRequirementsEditor(mountedSetId, activeTab);
      }
    } else {
      void renderStarterSetList(activeTab);
    }
  });

  const onSidebarMqChange = () => {
    if (disposed) return;
    updateLibraryChrome();
  };
  sidebarMq.addEventListener('change', onSidebarMqChange);

  syncRoute();

  return () => {
    disposed = true;
    host.removeEventListener('sedr:library-route', onLibraryRoute);
    sidebarMq.removeEventListener('change', onSidebarMqChange);
    chromeActionsCleanup?.();
    unbindNav();
    unbindLocale?.();
    contentRenderToken += 1;
    host.replaceChildren();
  };
}
